import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { Home, Building2, Package, Piano, Truck, ArrowRight, Check } from "lucide-react";
import { createServiceSchema } from "../seo/schemas";

const SERVICES_BREADCRUMB = [{ label: "Services", href: "/services" }];

const ServicesPage = () => {
  const services = [
    {
      icon: Home,
      title: "House Removals",
      description: "Complete house moving service for homes of all sizes, from studio flats to large family houses.",
      features: ["Packing service available", "Furniture dismantling", "Same-day availability", "Fully insured"],
      href: "/services/house-removals",
      image: "https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/d9b70dc68cc964050d6b7582cabdba6cd06668dbb9ef9a403050982926bf0037.png"
    },
    {
      icon: Building2,
      title: "Office Removals",
      description: "Professional business relocations with minimal disruption to your operations.",
      features: ["Weekend moves available", "IT equipment handling", "Flexible scheduling", "Project management"],
      href: "/services/office-removals",
      image: "https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/acf49cfebb293b7cef04a42dfc41de23d515e47416b9f16c3c274dc10541f317.png"
    },
    {
      icon: Truck,
      title: "Man and Van",
      description: "Affordable option for smaller moves, single items, and student relocations.",
      features: ["Same-day available", "From £50/hour", "Load & unload included", "Fully insured"],
      href: "/services/man-and-van",
      image: "https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/d9b70dc68cc964050d6b7582cabdba6cd06668dbb9ef9a403050982926bf0037.png"
    },
    {
      icon: Package,
      title: "Storage Solutions",
      description: "Secure, climate-controlled storage facilities for short or long-term needs.",
      features: ["24/7 security", "Climate controlled", "Flexible terms", "Easy access"],
      href: "/services/storage",
      image: "https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/a879bd00e5dab9522432a50f08568dc566f8ca7d78c427daf5c1df7c7bca5622.png"
    },
    {
      icon: Piano,
      title: "Specialist Moves",
      description: "Expert handling of pianos, antiques, artwork, and other valuable or fragile items.",
      features: ["Trained specialists", "Custom packaging", "Insurance included", "White glove service"],
      href: "/services/specialist-moves",
      image: "https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/26efd78509a621f4e14a9f19d36c95b199df64a5d0c23447566e6e566450c850.png"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Our Services | Professional Removal Services | Hitech Transit"
        description="From house moves to office relocations, storage and specialist transport. Hitech Transit offers a full range of professional removal services in Cardiff & South Wales."
        canonical="/services"
        jsonLd={createServiceSchema("Removal Services", "Professional house and office removal services in Cardiff and South Wales", "/services")}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={SERVICES_BREADCRUMB} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Our Services
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Professional Removal Services in Cardiff
              </h1>
              <p className="text-slate-300 text-lg">
                From simple house moves to complex commercial relocations, we have the expertise 
                and equipment to handle any moving challenge.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="section-padding">
          <div className="container-max">
            <div className="space-y-16">
              {services.map((service, index) => (
                <div 
                  key={service.title || service.name || `service-${index}`} 
                  className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="w-14 h-14 bg-[#FF5A1F]/10 flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-[#FF5A1F]" />
                    </div>
                    <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, i) => (
                        <li key={`star-${i}`} className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={service.href}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`service-link-${index}`}
                    >
                      Learn More
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-[#FF5A1F]">
          <div className="container-max text-center">
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/90 text-lg max-w-2xl mx-auto mb-8">
              Get an instant quote in under 60 seconds or speak to our team for expert advice.
            </p>
            <Link to="/instant-quote" className="bg-[#0F172A] text-white font-semibold px-8 py-4 inline-flex items-center gap-2 hover:bg-slate-800 transition-colors" data-testid="services-cta-btn">
              Get Instant Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
